'use client';

import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 100;

/**
 * Tracks Gladly chat availability via `getAvailability` and `availability:change`.
 * Returns `null` until a read succeeds. A throw before init settles is retried;
 * a read that never succeeds stays `null` so surrounding content can keep rendering.
 *
 * @returns {'AVAILABLE'|'UNAVAILABLE_OFFICE_CLOSED'|'UNAVAILABLE_BUSY'|null}
 */
export const useGladlyAvailability = () => {
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    /** @type {ReturnType<typeof setInterval> | undefined} */
    let intervalId;
    let unsubscribe = () => {};

    let hasAvailability = false;
    let isListening = false;

    const handleAvailabilityChange = status => {
      setAvailability(status);
    };

    // The loader replaces the stub with the real SDK before ChatButton's init
    // promise settles. getAvailability and on exist in that window and throw
    // until init resolves, so method presence is not readiness.
    const sync = () => {
      const { Gladly } = window;
      if (!Gladly) return false;

      if (!hasAvailability && typeof Gladly.getAvailability === 'function') {
        try {
          setAvailability(Gladly.getAvailability());
          hasAvailability = true;
        } catch {
          // Init has not settled; retry on the next poll.
        }
      }

      if (!isListening && typeof Gladly.on === 'function') {
        try {
          Gladly.on('availability:change', handleAvailabilityChange);
          isListening = true;
          unsubscribe = () => {
            if (typeof Gladly.off === 'function') {
              Gladly.off('availability:change', handleAvailabilityChange);
            }
          };
        } catch {
          // Init has not settled; retry on the next poll.
        }
      }

      return hasAvailability && isListening;
    };

    if (!sync()) {
      intervalId = setInterval(() => {
        if (!sync()) return;
        clearInterval(intervalId);
        intervalId = undefined;
      }, POLL_INTERVAL_MS);
    }

    return () => {
      clearInterval(intervalId);
      try {
        unsubscribe();
      } catch {
        // Unmount should not surface a Gladly error.
      }
    };
  }, []);

  return availability;
};
